import { Problem } from '../../types/problem';

export const arrayProblems: Problem[] = [
  {
    id: 'array-1',
    title: 'Find Maximum Element',
    description: `Write a recursive function to find the maximum element in an array.

**Example 1:**
Input: nums = [1, 4, 3, 7, 2, 5]
Output: 7

**Example 2:**
Input: nums = [-1, -5, -2, -8, -3]
Output: -1

**Constraints:**
- Array length will be at least 1
- Array can contain both positive and negative integers`,
    difficulty: 'Easy',
    tags: ['array', 'recursion', 'divide-and-conquer'],
    hints: [
      'Think about the base case when the array has only one element',
      'Consider dividing the array into two parts and solving recursively',
      'Use Math.max() to compare results from recursive calls'
    ],
    starterCode: `function findMax(arr: number[]): number {
  // Your code here
}`,
    solution: `function findMax(arr: number[]): number {
  // Base case: if array has only one element
  if (arr.length === 1) {
    return arr[0];
  }
  
  // Divide array into two parts
  const mid = Math.floor(arr.length / 2);
  const leftMax = findMax(arr.slice(0, mid));
  const rightMax = findMax(arr.slice(mid));
  
  // Return maximum of the two parts
  return Math.max(leftMax, rightMax);
}`,
    testCases: [
      {
        input: [[1, 4, 3, 7, 2, 5]],
        output: 7
      },
      {
        input: [[-1, -5, -2, -8, -3]],
        output: -1
      },
      {
        input: [[42]],
        output: 42
      },
      {
        input: [[1, 1, 1, 1]],
        output: 1
      }
    ]
  },
  {
    id: 'array-2',
    title: 'Count Elements Greater Than Average',
    description: `Write a function that counts the number of elements in an array that are greater than the average of all elements.

**Example 1:**
Input: nums = [1, 2, 3, 4, 5]
Output: 2 (elements 4 and 5 are greater than average 3)

**Example 2:**
Input: nums = [2, 2, 2, 2]
Output: 0 (no elements are greater than average 2)

**Constraints:**
- Array length will be at least 1
- Array will contain only integers`,
    difficulty: 'Medium',
    tags: ['array', 'math', 'counting'],
    hints: [
      'First calculate the average of all elements',
      'Then count elements greater than average',
      'Be careful with integer division'
    ],
    starterCode: `function countAboveAverage(arr: number[]): number {
  // Your code here
}`,
    solution: `function countAboveAverage(arr: number[]): number {
  // Calculate average
  const average = arr.reduce((sum, num) => sum + num, 0) / arr.length;
  
  // Count elements greater than average
  return arr.filter(num => num > average).length;
}`,
    testCases: [
      {
        input: [[1, 2, 3, 4, 5]],
        output: 2
      },
      {
        input: [[2, 2, 2, 2]],
        output: 0
      },
      {
        input: [[1, 1, 1, 10]],
        output: 1
      }
    ]
  },
  {
    id: 'array-3',
    title: 'Find Peak Element',
    description: `Write a recursive function to find a peak element in an array. A peak element is an element that is strictly greater than its neighbors.
If there are multiple peaks, return the index of any peak element.

**Example 1:**
Input: nums = [1,2,3,1]
Output: 2
Explanation: 3 is a peak element and your function should return the index 2.

**Example 2:**
Input: nums = [1,2,1,3,5,6,4]
Output: 5
Explanation: 6 is a peak element and your function should return the index 5.

**Constraints:**
- First and last elements are compared with only one neighbor
- Array length will be at least 1`,
    difficulty: 'Medium',
    tags: ['array', 'recursion', 'binary-search', 'divide-and-conquer'],
    hints: [
      'Use binary search approach',
      'Compare middle element with its neighbors',
      'If middle element is not a peak, recurse on the half that must contain a peak'
    ],
    starterCode: `function findPeakElement(nums: number[]): number {
  // Your code here
}`,
    solution: `function findPeakElement(nums: number[]): number {
  // Base case: if array has only one element
  if (nums.length === 1) {
    return 0;
  }
  
  // Find middle index
  const mid = Math.floor(nums.length / 2);
  
  // If middle element is a peak, return its index
  if ((mid === 0 || nums[mid] > nums[mid - 1]) && (mid === nums.length - 1 || nums[mid] > nums[mid + 1])) {
    return mid;
  }
  
  // If middle element is not a peak, recurse on the half that must contain a peak
  if (mid > 0 && nums[mid] < nums[mid - 1]) {
    return findPeakElement(nums.slice(0, mid));
  } else {
    return mid + findPeakElement(nums.slice(mid + 1));
  }
}`,
    testCases: [
      {
        input: [[1,2,3,1]],
        output: 2
      },
      {
        input: [[1,2,1,3,5,6,4]],
        output: 5
      },
      {
        input: [[1]],
        output: 0
      },
      {
        input: [[1,2]],
        output: 1
      }
    ]
  },
  {
    id: 'array-4',
    title: 'Recursive Array Rotation',
    description: `Write a recursive function to rotate an array to the right by k steps.

**Example 1:**
Input: nums = [1,2,3,4,5,6,7], k = 3
Output: [5,6,7,1,2,3,4]

**Example 2:**
Input: nums = [-1,-100,3,99], k = 2
Output: [3,99,-1,-100]

**Constraints:**
- k can be greater than array length
- Modify the array in-place
- Use recursion to solve this`,
    difficulty: 'Hard',
    tags: ['array', 'recursion', 'math'],
    hints: [
      'Consider what happens when k > array length',
      'Think about how to handle one rotation step recursively',
      'You might want to use modulo operator for k'
    ],
    starterCode: `function rotateArray(nums: number[], k: number): number[] {
  // Your code here
}`,
    solution: `function rotateArray(nums: number[], k: number): number[] {
  // Handle k > array length
  k = k % nums.length;
  
  // Reverse helper function
  function reverse(arr: number[], start: number, end: number): void {
    while (start < end) {
      [arr[start], arr[end]] = [arr[end], arr[start]];
      start++;
      end--;
    }
  }
  
  // Reverse entire array
  reverse(nums, 0, nums.length - 1);
  
  // Reverse first k elements
  reverse(nums, 0, k - 1);
  
  // Reverse remaining elements
  reverse(nums, k, nums.length - 1);
  
  return nums;
}`,
    testCases: [
      {
        input: [[1,2,3,4,5,6,7], 3],
        output: [5,6,7,1,2,3,4]
      },
      {
        input: [[-1,-100,3,99], 2],
        output: [3,99,-1,-100]
      },
      {
        input: [[1], 1],
        output: [1]
      }
    ]
  },
  {
    id: 'array-5',
    title: 'Recursive Matrix Spiral',
    description: `Write a recursive function that returns all elements of an n x n matrix in spiral order.

**Example 1:**
Input: matrix = [[1,2,3],[4,5,6],[7,8,9]]
Output: [1,2,3,6,9,8,7,4,5]

**Example 2:**
Input: matrix = [[1,2],[3,4]]
Output: [1,2,4,3]

**Constraints:**
- Matrix will be square (n x n)
- n >= 1
- Use recursion to traverse the matrix`,
    difficulty: 'Hard',
    tags: ['array', 'recursion', 'matrix', '2d-array'],
    hints: [
      'Think about how to handle the outer layer first',
      'After processing outer layer, you\'ll have a smaller matrix',
      'Base case would be a 1x1 or empty matrix'
    ],
    starterCode: `function spiralOrder(matrix: number[][]): number[] {
  // Your code here
}`,
    solution: `function spiralOrder(matrix: number[][]): number[] {
  // Base case: if matrix is empty
  if (matrix.length === 0) {
    return [];
  }
  
  // Get first row
  const firstRow = matrix.shift();
  
  // Get last element of each remaining row
  const lastElements = matrix.map(row => row.pop());
  
  // Get last row in reverse order
  const lastRow = matrix.pop() ? matrix.pop().reverse() : [];
  
  // Get first element of each remaining row
  const firstElements = matrix.map(row => row.shift());
  
  // Recurse on remaining matrix
  const remaining = spiralOrder(matrix);
  
  // Combine results
  return [...firstRow, ...lastElements, ...lastRow, ...firstElements, ...remaining];
}`,
    testCases: [
      {
        input: [[[1,2,3],[4,5,6],[7,8,9]]],
        output: [1,2,3,6,9,8,7,4,5]
      },
      {
        input: [[[1,2],[3,4]]],
        output: [1,2,4,3]
      },
      {
        input: [[[1]]],
        output: [1]
      },
      {
        input: [[[1,2,3,4],[5,6,7,8],[9,10,11,12],[13,14,15,16]]],
        output: [1,2,3,4,8,12,16,15,14,13,9,5,6,7,11,10]
      }
    ]
  }
];